// MP.7 Hotfix R6 · Avatar dropdown menu · V12 Achromatic Strict.
// Sostituisce il link diretto /profile del topbar con un dropdown che
// contiene 8 shortcut account + divider + logout. Il gruppo "Account"
// è stato rimosso dalla sidebar (vedi `v12_user_nav.js` R6 note).
//
// Keyboard support · ESC chiude · click outside chiude · focus-visible ring.
// Mobile first-pass · dropdown width 260px fissa · funziona anche sotto 640px
// (preview area è comunque desktop).
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
    ArrowRight, Coins, FileText, KeyRound, LogOut, Mail, ScrollText,
    ShieldCheck, Tag, User, UserCircle2, Languages,
} from "lucide-react";

import { useAuth } from "@/context/AuthContext";
import { useTranslation, SUPPORTED_LOCALES } from "@/i18n/I18nContext";

// SSOT · 8 link account · path reali presi da App.js (R5 ha verificato routes).
const ACCOUNT_LINKS = [
    { id: "profilo",          label: "Profilo",             to: "/app/account/profilo",         icon: User },
    { id: "crediti",          label: "Crediti",             to: "/app/account/crediti",         icon: Coins },
    { id: "storico-crediti",  label: "Storico crediti",     to: "/app/account/storico-crediti", icon: ScrollText },
    { id: "fatturazione",     label: "Fatturazione",        to: "/app/account/fatturazione",    icon: FileText },
    { id: "prezzi",           label: "Prezzi (upgrade)",    to: "/app/subscription",            icon: Tag },
    { id: "email",            label: "Email transazionali", to: "/app/account/email-preferenze", icon: Mail },
    { id: "privacy",          label: "Dati & Privacy",      to: "/app/account/privacy",         icon: ShieldCheck },
    { id: "api-keys",         label: "API Keys personali",  to: "/app/account/api-keys",        icon: KeyRound },
];

export default function V12UserAvatarMenu() {
    const { user, logout } = useAuth();
    const { locale, setLocale } = useTranslation();
    const [open, setOpen] = useState(false);
    const containerRef = useRef(null);

    const close = useCallback(() => setOpen(false), []);

    // Wave VII.A R1b · credits badge · stesso pattern del V12UserTopbar
    // (user.credits dall'AuthContext + ascolto evento `credits:changed`
    // per update istantaneo post-consume/post-stripe-success). Zero API
    // extra: il wallet è già popolato da AuthContext via /api/auth/me.
    const [liveCredits, setLiveCredits] = useState(null);
    useEffect(() => {
        function _handler(e) {
            const d = e?.detail || {};
            if (typeof d.new_balance === "number") setLiveCredits(d.new_balance);
        }
        window.addEventListener("credits:changed", _handler);
        return () => window.removeEventListener("credits:changed", _handler);
    }, []);
    const rawCredits = (liveCredits !== null)
        ? liveCredits
        : (user && typeof user.credits === "number")
            ? user.credits
            : null;
    const formattedCredits = (rawCredits === null)
        ? "—"
        : new Intl.NumberFormat("it-IT").format(rawCredits);

    // Click outside + ESC handler
    useEffect(() => {
        if (!open) return;
        function _onClick(e) {
            if (!containerRef.current) return;
            if (!containerRef.current.contains(e.target)) close();
        }
        function _onKey(e) {
            if (e.key === "Escape") close();
        }
        document.addEventListener("mousedown", _onClick);
        document.addEventListener("keydown", _onKey);
        return () => {
            document.removeEventListener("mousedown", _onClick);
            document.removeEventListener("keydown", _onKey);
        };
    }, [open, close]);

    const displayName = user?.name || user?.email || "Account";
    const displayEmail = user?.email || "";

    const _onLogout = useCallback(() => {
        close();
        if (typeof logout === "function") logout();
    }, [close, logout]);

    return (
        <div ref={containerRef} className="relative" data-testid="v12-user-avatar-menu-root">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={open ? "true" : "false"}
                className="inline-flex items-center gap-2
                           px-2 h-9 rounded-md border v12-border
                           v12-card text-neutral-200
                           hover:border-[#CCCCCC]/30 hover:text-neutral-100
                           transition-colors duration-200"
                data-testid="v12-user-topbar-profile-button"
            >
                <UserCircle2 size={14} />
                <span className="hidden md:inline text-xs truncate max-w-[140px]">
                    {displayName}
                </span>
            </button>

            {open && (
                <div
                    role="menu"
                    aria-label="Account menu"
                    data-testid="v12-user-avatar-menu-dropdown"
                    className="absolute right-0 top-[calc(100%+6px)]
                               w-64 rounded-md border v12-border
                               bg-neutral-900 shadow-lg shadow-black/40
                               overflow-hidden z-30"
                >
                    {/* Header · avatar grande + email */}
                    <div
                        className="px-3 py-3 border-b v12-border flex items-start gap-2.5"
                        data-testid="v12-user-avatar-menu-header"
                    >
                        <div className="w-9 h-9 rounded-full border v12-border
                                        flex items-center justify-center
                                        bg-neutral-800 text-neutral-200 shrink-0">
                            <UserCircle2 size={20} />
                        </div>
                        <div className="min-w-0">
                            <div
                                className="text-xs font-medium text-neutral-100 truncate"
                                data-testid="v12-user-avatar-menu-name"
                            >
                                {displayName}
                            </div>
                            {displayEmail && (
                                <div
                                    className="text-[11px] text-neutral-500 truncate"
                                    data-testid="v12-user-avatar-menu-email"
                                >
                                    {displayEmail}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Wave VII.A R1b · Credits badge row + CTA Ricarica.
                        Separato dal header con bordo sottile · hover-free row
                        (non è un link intero · solo il button Ricarica è
                        interattivo). Mostra "—" quando rawCredits null (loading
                        o fetch error · AuthContext ancora in idle). */}
                    <div
                        className="px-3 py-2.5 border-b v12-border flex items-center gap-2"
                        data-testid="v12-user-avatar-menu-credits-row"
                    >
                        <Coins size={14} className="stroke-zinc-500 shrink-0" />
                        <span
                            className="text-xs text-neutral-200 tabular-nums"
                            data-testid="v12-user-avatar-menu-credits-value"
                        >
                            {formattedCredits}
                        </span>
                        <span className="text-[11px] text-neutral-500">crediti</span>
                        <Link
                            to="/credits/packs"
                            onClick={() => close()}
                            data-testid="v12-user-avatar-menu-topup"
                            className="ml-auto inline-flex items-center gap-1
                                       px-2 py-1 rounded border v12-border
                                       bg-neutral-800 text-[11px] text-neutral-100
                                       hover:bg-neutral-700 hover:border-[#CCCCCC]/30
                                       transition-colors duration-150"
                        >
                            Ricarica
                            <ArrowRight size={11} className="stroke-current" />
                        </Link>
                    </div>

                    {/* Account links · 8 shortcut */}
                    <div className="py-1" data-testid="v12-user-avatar-menu-links">
                        {ACCOUNT_LINKS.map((it) => {
                            const Icon = it.icon;
                            return (
                                <Link
                                    key={it.id}
                                    to={it.to}
                                    onClick={() => close()}
                                    role="menuitem"
                                    data-testid={`v12-user-avatar-menu-link-${it.id}`}
                                    className="flex items-center gap-2 px-3 py-2 text-xs
                                               text-neutral-300 hover:bg-neutral-800
                                               hover:text-neutral-100 transition-colors duration-150"
                                >
                                    <Icon size={14} className="stroke-zinc-500 shrink-0" />
                                    <span className="truncate">{it.label}</span>
                                </Link>
                            );
                        })}
                    </div>

                    {/* Divider + Logout · redundante con il button icon separato
                        nel topbar, ma UX standard per dropdown avatar. */}
                    <div className="border-t v12-border py-1">
                        {/* Phase IX.F.4 · A5 · Language switcher · locali supportate (IT, EN).
                            Documentato nel SEAL · Phase X aggiungerà le 40 lingue PRD V10. */}
                        <div
                            className="flex items-center gap-2 px-3 py-2"
                            data-testid="v12-user-avatar-menu-language-row"
                        >
                            <Languages size={14} className="stroke-zinc-500 shrink-0" />
                            <span className="text-[11px] text-neutral-500">Lingua</span>
                            <select
                                value={locale}
                                onChange={(e) => setLocale(e.target.value)}
                                data-testid="v12-user-avatar-menu-language-select"
                                className="ml-auto bg-neutral-800 border v12-border rounded px-1.5 py-0.5 text-[11px] text-neutral-200 focus:outline-none"
                            >
                                {SUPPORTED_LOCALES.map((loc) => (
                                    <option key={loc} value={loc}>{loc.toUpperCase()}</option>
                                ))}
                            </select>
                        </div>
                        <button
                            type="button"
                            onClick={_onLogout}
                            role="menuitem"
                            data-testid="v12-user-avatar-menu-logout"
                            className="w-full flex items-center gap-2 px-3 py-2 text-xs
                                       text-neutral-400 hover:bg-neutral-800
                                       hover:text-neutral-100 transition-colors duration-150"
                        >
                            <LogOut size={14} className="stroke-zinc-500 shrink-0" />
                            <span>Logout</span>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
